import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { lookups, taskRepo } from '../../data/db';
import { tasksApi, workflowsApi, usersApi, extractOne, extractList } from '../../api/facades';
import { Status, Task } from '../../types';
import { toast } from 'sonner';

const inputClass =
  'w-full rounded-lg border border-solid border-gray-300 bg-white dark:bg-slate-800 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

const TaskEditPage: React.FC = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numId = taskId ? parseInt(taskId, 10) : NaN;
  const isApiTask = !Number.isNaN(numId) && String(numId) === String(taskId);

  const users = useMemo(() => lookups.users(), []);
  const statuses = useMemo(() => lookups.taskStatuses(), []);

  const existing = useMemo(() => (taskId && !isApiTask ? taskRepo.get(taskId) : undefined), [taskId, isApiTask]);

  const { data: apiTaskRes, isLoading: apiLoading } = useQuery({
    queryKey: ['tasks', numId],
    queryFn: () => tasksApi.get(numId),
    enabled: isApiTask,
  });
  const apiTask = extractOne(apiTaskRes);

  const { data: workflowsRes } = useQuery({ queryKey: ['workflows'], queryFn: () => workflowsApi.list({ per_page: 100 }), enabled: isApiTask });
  const workflows = extractList(workflowsRes);

  const { data: usersApiRes } = useQuery({ queryKey: ['users'], queryFn: () => usersApi.list({ per_page: 100 }), enabled: isApiTask });
  const apiUsers = useMemo(() => {
    const d = usersApiRes?.data?.data;
    return Array.isArray(d?.data) ? d.data : Array.isArray(d) ? d : [];
  }, [usersApiRes]);

  const [title, setTitle] = useState(existing?.title ?? '');
  const [assignedUserId, setAssignedUserId] = useState(existing?.assignedTo?.id ?? '');
  const [dueDate, setDueDate] = useState(existing?.dueDate ?? new Date().toISOString());
  const [statusId, setStatusId] = useState<Status['id']>(existing?.status?.id ?? statuses[0]?.id ?? 'pending');
  const [priority, setPriority] = useState<Task['priority']>(existing?.priority ?? 'medium');

  const [task_subject, setTaskSubject] = useState('');
  const [project_status, setProjectStatus] = useState('Pending');
  const [priorityApi, setPriorityApi] = useState('Medium');
  const [depends_on_task_id, setDependsOnTaskId] = useState<number | ''>('');
  const [user_ids, setUserIds] = useState<number[]>([]);
  const [workflowTasks, setWorkflowTasks] = useState<any[]>([]);

  useEffect(() => {
    if (apiTask && typeof apiTask === 'object') {
      const t = apiTask as any;
      setTaskSubject(t.task_subject ?? '');
      setProjectStatus(t.project_status ?? 'Pending');
      setPriorityApi(t.priority ?? 'Medium');
      setDependsOnTaskId(t.depends_on_task_id ?? '');
      setUserIds((t.members ?? []).map((m: any) => m.user_id));
    }
  }, [apiTask]);

  useEffect(() => {
    if (apiTask && (apiTask as any).workflow_id && workflowsRes) {
      const wfId = (apiTask as any).workflow_id;
      workflowsApi.get(wfId).then((r: any) => {
        const w = r?.data?.data ?? r?.data;
        setWorkflowTasks(w?.tasks ?? []);
      });
    }
  }, [apiTask, workflowsRes]);

  const updateApi = useMutation({
    mutationFn: () =>
      tasksApi.update(numId, {
        task_subject: task_subject.trim(),
        project_status: project_status || null,
        priority: priorityApi || null,
        depends_on_task_id: depends_on_task_id === '' ? null : depends_on_task_id,
        user_ids,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      toast.success('Task updated.');
      navigate((apiTask as any)?.workflow_id ? `/dashboard/workflows/${(apiTask as any).workflow_id}` : '/dashboard/tasks');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || e?.message || 'Update failed.'),
  });

  const toggleUser = (id: number) => {
    setUserIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  if (isApiTask) {
    if (apiLoading || !apiTask) {
      return (
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <p className="text-slate-500">Loading…</p>
          <Button variant="secondary" className="mt-4" onClick={() => navigate(-1)}>Back</Button>
        </div>
      );
    }
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <h1 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-4">Edit task</h1>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!task_subject.trim()) return;
            updateApi.mutate();
          }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl"
        >
          <div className="md:col-span-2">
            <FormField label="Task name" hint="Required">
              <input className={inputClass} value={task_subject} onChange={(e) => setTaskSubject(e.target.value)} required />
            </FormField>
          </div>
          <FormField label="Status">
            <select className={inputClass} value={project_status} onChange={(e) => setProjectStatus(e.target.value)}>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
            </select>
          </FormField>
          <FormField label="Priority">
            <select className={inputClass} value={priorityApi} onChange={(e) => setPriorityApi(e.target.value)}>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
          </FormField>
          <FormField label="Depends on task">
            <select className={inputClass} value={depends_on_task_id === '' ? '' : depends_on_task_id} onChange={(e) => setDependsOnTaskId(e.target.value === '' ? '' : Number(e.target.value))}>
              <option value="">— None —</option>
              {workflowTasks.filter((t: any) => t.id !== numId).map((t: any) => (
                <option key={t.id} value={t.id}>{t.task_subject} (#{t.id})</option>
              ))}
            </select>
          </FormField>
          <div className="md:col-span-2">
            <FormField label="Assignees">
              <div className="flex flex-wrap gap-2">
                {apiUsers.map((u: any) => (
                  <label key={u.id} className="inline-flex items-center gap-1 text-sm">
                    <input type="checkbox" checked={user_ids.includes(u.id)} onChange={() => toggleUser(u.id)} />
                    <span>{u.name ?? u.email ?? u.id}</span>
                  </label>
                ))}
              </div>
            </FormField>
          </div>
          <div className="md:col-span-2 flex gap-2">
            <Button variant="secondary" type="button" onClick={() => navigate(-1)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={!task_subject.trim() || updateApi.isPending}>{updateApi.isPending ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      </div>
    );
  }

  if (!taskId || !existing) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <h1 className="text-2xl font-bold text-slate-700 dark:text-slate-200 mb-2">Task not found</h1>
        <Button variant="primary" onClick={() => navigate('/dashboard/tasks')}>Back to Tasks</Button>
      </div>
    );
  }

  const status = statuses.find((s) => s.id === statusId) ?? existing.status;
  const assignedTo = users.find((u) => u.id === assignedUserId);
  const canSubmit = title.trim() && dueDate.trim();

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    taskRepo.update(existing.id, {
      title: title.trim(),
      assignedTo,
      dueDate: dueDate.trim(),
      status,
      priority,
    });
    navigate(`/dashboard/tasks/${existing.id}`);
  };

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Update task fields (local task).</p>
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <FormField label="Title">
            <input className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} />
          </FormField>
        </div>
        <FormField label="Assigned to" hint="Optional">
          <select className={inputClass} value={assignedUserId} onChange={(e) => setAssignedUserId(e.target.value)}>
            <option value="">Unassigned</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.username}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Due date (ISO)">
          <input className={inputClass} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </FormField>
        <FormField label="Status">
          <select className={inputClass} value={statusId} onChange={(e) => setStatusId(e.target.value)}>
            {statuses.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Priority">
          <select className={inputClass} value={priority} onChange={(e) => setPriority(e.target.value as Task['priority'])}>
            <option value="low">low</option>
            <option value="medium">medium</option>
            <option value="high">high</option>
            <option value="urgent">urgent</option>
          </select>
        </FormField>
        <div className="md:col-span-2 flex items-center justify-end gap-2 mt-2">
          <Button variant="secondary" onClick={() => navigate(-1)}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!canSubmit}>Save</Button>
        </div>
      </form>
    </div>
  );
};

export default TaskEditPage;
